import {
  AddShoppingCart,
  EventAvailable as EventAvailableIcon,
  Inventory,
  RestorePageOutlined as RestorePageIcon,
} from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
} from "@mui/material";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import { CartSingleton } from "../SharedComponents/CartSingleton";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { IAssignment } from "./controller";

export interface ManagedAssignmentRowDelegate {
  didClickArchive(applicationId: number): void;
  didClickCopyAssignment(applicationId: number): void;
  didClickCopyAnonAssignment(applicationId: number): void;
  didClickCopyGenericAnonAssignment(applicationId: number): void;
  didClickReimburse(applicationId: number): void;
  didClickEditDueDate(applicationId: number): void;
}

interface Props {
  assignment: IAssignment;
  delegate: ManagedAssignmentRowDelegate;
  navigationHooks: NavigateFunction;
}

interface State {
  anchorEl: null | HTMLElement;
  cartQuantity: number;
}

class ManagedAssignmentRow extends React.Component<Props, State> {
  private cartListener = () => this.syncCartQuantity();
  private isUnmounted = false;

  constructor(props: Props) {
    const existingItem = props.assignment.instrumentId
      ? CartSingleton.shared.getCartInstrument(props.assignment.instrumentId)
      : null;
    const startingQuantity = existingItem?.quantity ?? 0;

    super(props);
    this.state = {
      anchorEl: null,
      cartQuantity: startingQuantity,
    };
  }

  componentDidMount(): void {
    this.isUnmounted = false;
    CartSingleton.shared.addCartUpdatedListener(this.cartListener);
    if (this.props.assignment.instrumentId) {
      void CartSingleton.shared
        .ensureLoaded()
        .then(() => {
          this.syncCartQuantity();
        })
        .catch(() => {
          this.syncCartQuantity();
        });
    }
  }

  componentWillUnmount(): void {
    this.isUnmounted = true;
    CartSingleton.shared.removeCartUpdatedListener(this.cartListener);
  }

  handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  handleMenuItemClick = (action: () => void) => {
    this.handleMenuClose();
    action();
  };

  didClickAddToCart() {
    if (this.props.assignment?.instrumentId) {
      const quantity = this.state.cartQuantity > 0 ? this.state.cartQuantity : 1;
      void CartSingleton.shared
        .setCartInstrument(this.props.assignment.instrumentId, quantity)
        .catch(() => undefined);
    }
    this.props.navigationHooks("/checkout/review");
  }

  private syncCartQuantity() {
    if (this.isUnmounted || !this.props.assignment.instrumentId) {
      return;
    }

    const item = CartSingleton.shared.getCartInstrument(
      this.props.assignment.instrumentId
    );
    const quantity = item?.quantity ?? 0;

    if (quantity !== this.state.cartQuantity) {
      this.setState({ cartQuantity: quantity });
    }
  }

  render() {
    const { assignment, delegate } = this.props;
    const menuOpen = Boolean(this.state.anchorEl);

    return (
      <TableRow>
        <TableCell>{assignment.title}</TableCell>
        <TableCell align="right">
          {assignment.dueDate.toLocaleString(undefined, {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </TableCell>
        <TableCell align="right">{assignment.instrumentTitle}</TableCell>
        <TableCell align="right">
          {assignment.totalApplicationCount ?? "∞"}
        </TableCell>
        <TableCell align="right">{assignment.doneApplicationCount}</TableCell>
        <TableCell align="right">
          {assignment.notDoneApplicationCount}
        </TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="actions"
            size="medium"
            sx={{
              backgroundColor: "grey.200",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "grey.300",
              },
            }}
            onClick={this.handleMenuClick}
            title="Mais opções"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={this.state.anchorEl}
            open={menuOpen}
            onClose={this.handleMenuClose}
          >
            {assignment.totalApplicationCount > 0 &&
              assignment.allowAuthenticatedLink && (
                <MenuItem
                  onClick={() =>
                    this.handleMenuItemClick(() =>
                      delegate.didClickCopyAssignment(assignment.applicationId)
                    )
                  }
                >
                  <ListItemIcon>
                    <ContentCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Copiar link identificado" />
                </MenuItem>
              )}

            {assignment.totalApplicationCount > 0 &&
              assignment.allowAnonymousLink && (
                <MenuItem
                  onClick={() =>
                    this.handleMenuItemClick(() =>
                      delegate.didClickCopyGenericAnonAssignment(
                        assignment.applicationId
                      )
                    )
                  }
                >
                  <ListItemIcon>
                    <LinkOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Copiar link anônimo" />
                </MenuItem>
              )}

            <MenuItem
              onClick={() =>
                this.handleMenuItemClick(() =>
                  delegate.didClickEditDueDate(assignment.applicationId)
                )
              }
            >
              <ListItemIcon>
                <EventAvailableIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Atualizar prazo" />
            </MenuItem>

            {assignment.totalApplicationCount < 1 && (
              <MenuItem
                onClick={() =>
                  this.handleMenuItemClick(() => this.didClickAddToCart())
                }
              >
                <ListItemIcon>
                  <AddShoppingCart fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Comprar mais licenças" />
              </MenuItem>
            )}

            <Divider />

            {assignment.notDoneApplicationCount > 0 && (
              <MenuItem
                onClick={() =>
                  this.handleMenuItemClick(() =>
                    delegate.didClickReimburse(assignment.applicationId)
                  )
                }
                disabled={assignment.notDoneApplicationCount === 0}
              >
                <ListItemIcon>
                  <RestorePageIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Ressarcir licenças" />
              </MenuItem>
            )}

            <MenuItem
              onClick={() =>
                this.handleMenuItemClick(() =>
                  delegate.didClickArchive(assignment.applicationId)
                )
              }
            >
              <ListItemIcon>
                <Inventory fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Arquivar aplicações" />
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
    );
  }
}

export default withNavigationHooks(ManagedAssignmentRow);
